import { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";
import { FormItemCommonProps, FormItemContent } from "./common";
import { Checkbox, CheckboxProps } from "@/components/ui/checkbox";
import { FormField } from "@/components/ui/form";
import { callAll, cn } from "@/lib/utils";

export type FormCheckboxProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
> = {
  form: UseFormReturn<TFieldValues, TContext, TTransformedValues>;
  name: TName;
} & FormItemCommonProps &
  Omit<CheckboxProps, "onCheckedChange" | "form" | "checkd"> & {
    onChange?: CheckboxProps["onCheckedChange"];
  } & {
    inline?: boolean;
    placeholder?: string;
    isActive?: boolean;
    noPadding?: boolean;
  };

export type FormCheckboxCommonProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TContext = any,
  TTransformedValues extends FieldValues | undefined = undefined,
> = Omit<
  FormCheckboxProps<TFieldValues, TName, TContext, TTransformedValues>,
  "form" | "name" | "label"
> &
  Partial<Pick<FormItemCommonProps, "label">>;

const InlineWrap = ({
  children,
  inline,
  isActive,
  placeholder,
  className,
  ...props
}: {
  children?: React.ReactNode;
  inline?: boolean;
  isActive?: boolean;
  placeholder?: string;
} & React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
>) => {
  if (!inline) return children;
  return (
    <div
      {...props}
      className={cn(
        "border gap-2 rounded-md flex items-center text-muted-alt h-8 px-2",
        // inline &&
        isActive && "border-azure bg-azure/5 text-azure hover:bg-azure/10",
        // inline &&
        !isActive && "border-aqua hover:bg-aqua/5",
        // open &&
        "focus:ring-1 focus:ring-azure",
        className,
      )}
    >
      {placeholder && <span>{placeholder}</span>}
      {children}
    </div>
  );
};

export function FormCheckbox<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TContext,
  TTransformedValues extends FieldValues | undefined,
>({
  form,
  name,
  label,
  container,
  onChange,
  onBlur,
  inline,
  isActive,
  noPadding,
  placeholder,
  ...props
}: FormCheckboxProps<TFieldValues, TName, TContext, TTransformedValues>) {
  // Automatically disable clear button for checkboxes unless explicitly overridden
  const containerWithClear = {
    ...container,
    clear: container?.clear === undefined ? null : container?.clear,
  };

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItemContent
          {...containerWithClear}
          label={label}
          inline={container?.inline ?? inline}
        >
          <InlineWrap
            placeholder={inline ? label : placeholder}
            inline={inline}
            isActive={isActive}
            tabIndex={0}
          >
            <Checkbox
              {...(field as any)}
              type={"button"}
              checked={field.value ?? false}
              {...props}
              className={cn(!inline && !noPadding && "mt-2", props.className)}
              onCheckedChange={callAll(field.onChange, onChange)}
              onBlur={callAll(field.onBlur, onBlur)}
            />
          </InlineWrap>
        </FormItemContent>
      )}
    />
  );
}
